import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Search, MapPin, ShieldCheck, ChevronDown, Paintbrush2, ArrowRight, UserPlus } from "lucide-react";
import api, { API } from "@/lib/api";
import usePageMeta from "@/hooks/usePageMeta";
import { usePublicSettings } from "@/hooks/usePublicSettings";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const AU_STATES = ["NSW", "VIC", "QLD", "WA", "SA", "TAS", "ACT", "NT"];

function absolute(url) {
  if (!url) return null;
  return url.startsWith("http") ? url : `${BACKEND_URL}${url}`;
}

// Block G — ItemList JSON-LD for the directory page. Each item is a
// LocalBusiness pointer so Google understands these are individual businesses.
function buildDirectoryJsonLd(items) {
  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: "ColourMatch Pro — verified Australian painters",
    itemListElement: items.slice(0, 50).map((p, i) => ({
      "@type": "ListItem",
      position: i + 1,
      item: {
        "@type": ["LocalBusiness", "PaintingService"],
        "@id": `https://colourmatch.au/painters/${p.slug}`,
        name: p.business_name,
        url: `https://colourmatch.au/painters/${p.slug}`,
        image: absolute(p.card_image_url) || undefined,
        address: {
          "@type": "PostalAddress",
          addressLocality: p.hq_suburb,
          addressRegion: p.hq_state,
          postalCode: p.hq_postcode,
          addressCountry: "AU",
        },
      },
    })),
  };
}

async function track(slug, payload) {
  // Silent — never throw. Uses sendBeacon when available.
  try {
    const body = JSON.stringify(payload);
    const url = `${API}/painters/${encodeURIComponent(slug)}/track`;
    if (navigator.sendBeacon) {
      const blob = new Blob([body], { type: "application/json" });
      navigator.sendBeacon(url, blob);
      return;
    }
    await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body,
      keepalive: true,
    });
  } catch {
    /* swallow */
  }
}

// PLSH-FE-03 (2026-07-04): deterministic branded cover for painters without
// an uploaded hero/gallery image. Uses the painter's business_name initials
// on a hash-derived gradient so each card looks distinct rather than
// showing the same generic icon 20 times.
function PainterInitialsCover({ name, slug }) {
  const initials = (name || "?")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase() || "?";
  const gradients = [
    "from-brand-teal/85 to-brand-teal",
    "from-stone-800 to-stone-600",
    "from-brand-teal/70 to-stone-800",
    "from-amber-700/80 to-stone-800",
    "from-teal-700 to-brand-teal",
    "from-stone-900 to-brand-teal/85",
  ];
  const idx = Math.abs(
    (slug || name || "x").split("").reduce((a, c) => (a * 31 + c.charCodeAt(0)) | 0, 0),
  ) % gradients.length;
  return (
    <div
      data-testid="painter-card-initials-cover"
      className={`w-full h-full flex items-center justify-center bg-gradient-to-br ${gradients[idx]}`}
      aria-label={`${name} placeholder cover`}
    >
      <span className="text-white/95 font-bold tracking-wider" style={{ fontSize: "clamp(28px, 5vw, 44px)" }}>
        {initials}
      </span>
    </div>
  );
}


function PainterCard({ p, fromProject }) {
  const cardRef = useRef(null);
  const trackedRef = useRef(false);

  // Fire directory_card_view once when the card is first visible.
  useEffect(() => {
    const node = cardRef.current;
    if (!node || trackedRef.current) return;
    const io = new IntersectionObserver((entries) => {
      for (const e of entries) {
        if (e.isIntersecting && !trackedRef.current) {
          trackedRef.current = true;
          track(p.slug, {
            event: "directory_card_view",
            source: "directory",
            project_source: fromProject ? "workspace_cta" : null,
          });
          io.disconnect();
        }
      }
    }, { threshold: 0.4 });
    io.observe(node);
    return () => io.disconnect();
  }, [p.slug, fromProject]);

  const img = absolute(p.card_image_url);
  const detailUrl = `/painters/${p.slug}${fromProject ? `?from_project=${fromProject}` : ""}`;

  return (
    <Link
      to={detailUrl}
      ref={cardRef}
      data-testid={`painter-card-${p.slug}`}
      className="group block bg-white rounded-3xl border border-brand-border overflow-hidden hover:shadow-navy transition-all tap-spring"
    >
      <div className="aspect-[4/3] bg-stone-100 relative overflow-hidden">
        {img ? (
          <img
            src={img}
            alt={p.business_name}
            className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500"
            loading="lazy"
          />
        ) : (
          /* PLSH-FE-03 (2026-07-04): branded initials fallback replaces the
             identical paintbrush icon that used to appear on every card.
             Deterministic gradient (per-painter) keeps the directory
             visually varied without needing painter-supplied imagery. */
          <PainterInitialsCover name={p.business_name} slug={p.slug} />
        )}
        {p.verification_badges?.length > 0 && (
          <span className="absolute top-3 left-3 inline-flex items-center gap-1.5 bg-brand-navy text-white text-xs font-semibold px-3 py-1.5 rounded-full">
            <ShieldCheck size={13} /> Verified independent painter
          </span>
        )}
      </div>
      <div className="p-5">
        <h3 className="font-bold text-brand-ink text-lg leading-tight">{p.business_name}</h3>
        <div className="mt-2 flex items-center gap-1.5 text-sm text-brand-muted">
          <MapPin size={14} /> {p.hq_suburb}, {p.hq_state} {p.hq_postcode}
        </div>
        {p.service_scope?.length > 0 && (
          <div className="mt-4 flex flex-wrap gap-2">
            {p.service_scope.map((s) => (
              <span
                key={s}
                className="inline-flex items-center gap-1 bg-stone-100 text-brand-ink text-xs font-medium px-2.5 py-1 rounded-full capitalize"
              >
                {s}
              </span>
            ))}
          </div>
        )}
        <div className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-brand-teal group-hover:gap-2.5 transition-all">
          View profile <ArrowRight size={14} />
        </div>
      </div>
    </Link>
  );
}

export default function PaintersDirectory() {
  const [searchParams, setSearchParams] = useSearchParams();
  const fromProject = searchParams.get("from_project");
  const { settings } = usePublicSettings();
  const enabled = !!settings.PAINTERPRO_ENABLED;

  const [q, setQ] = useState(searchParams.get("q") || "");
  const [state, setState] = useState(searchParams.get("state") || "");
  const [scope, setScope] = useState(searchParams.get("scope") || "");
  const [sort, setSort] = useState(searchParams.get("sort") || "recent");
  const [page, setPage] = useState(Number(searchParams.get("page") || 1));
  const [data, setData] = useState({ items: [], total: 0, page: 1, page_size: 20 });
  const [busy, setBusy] = useState(true);
  const [err, setErr] = useState("");

  usePageMeta({
    title: "Find a painter in your area — ColourMatch",
    description:
      "Browse independent painters listed on ColourMatch. Each painter provides insurance and licence details, verified before listing. Request a quote with the colours you've chosen.",
    path: "/painters",
    // Block G — ItemList JSON-LD enumerating the visible painters.
    jsonLd: data?.items?.length > 0 ? buildDirectoryJsonLd(data.items) : null,
  });

  const query = useMemo(
    () => ({ q: q.trim(), state, scope, sort, page }),
    [q, state, scope, sort, page]
  );

  useEffect(() => {
    let alive = true;
    async function go() {
      setBusy(true);
      setErr("");
      try {
        const r = await api.get("/public/painters", {
          params: {
            ...(query.q ? { q: query.q } : {}),
            ...(query.state ? { state: query.state } : {}),
            ...(query.scope ? { scope: query.scope } : {}),
            sort: query.sort,
            page: query.page,
            page_size: 20,
          },
        });
        if (!alive) return;
        setData(r.data);
      } catch (e) {
        if (!alive) return;
        if (e?.response?.status === 404) {
          setErr("ColourMatch Pro is not yet available. Please check back soon.");
        } else {
          setErr("Could not load painters. Please try again.");
        }
        setData({ items: [], total: 0, page: 1, page_size: 20 });
      } finally {
        if (alive) setBusy(false);
      }
    }
    if (enabled) {
      go();
    } else {
      setBusy(false);
    }
    return () => {
      alive = false;
    };
  }, [query, enabled]);

  useEffect(() => {
    const next = {};
    if (q.trim()) next.q = q.trim();
    if (state) next.state = state;
    if (scope) next.scope = scope;
    if (sort && sort !== "recent") next.sort = sort;
    if (page > 1) next.page = String(page);
    if (fromProject) next.from_project = fromProject;
    setSearchParams(next, { replace: true });
  }, [q, state, scope, sort, page, fromProject, setSearchParams]);

  const totalPages = Math.max(1, Math.ceil((data.total || 0) / (data.page_size || 20)));

  return (
    <div className="bg-brand-bg min-h-screen">
      {/* Hero */}
      <section className="border-b border-brand-border bg-white">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-14 lg:py-20">
          <span className="inline-flex items-center gap-2 bg-stone-100 rounded-full px-3 py-1.5 text-xs font-semibold text-brand-teal">
            <ShieldCheck size={13} /> ColourMatch Pro
          </span>
          <h1 className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-brand-ink leading-[1.05]">
            Find a painter in your area
          </h1>
          <p className="mt-5 text-lg text-brand-muted max-w-2xl">
            Browse independent painters listed on ColourMatch. Each painter provides their insurance and licence details,
            verified before listing. Share your photo and chosen colours when you request a quote.
          </p>

          {fromProject && (
            <div
              data-testid="from-project-banner"
              className="mt-6 inline-flex items-center gap-2 bg-brand-teal/10 text-brand-teal text-sm font-medium px-4 py-2 rounded-full"
            >
              <Paintbrush2 size={14} />
              Painters will see your project photo and chosen colours.
            </div>
          )}

          {/* Homeowner ↔ painter connection · founder-locked core workflow.
              At the "who paints my project" decision point, homeowners can
              either choose a ColourMatch Pro business (below) OR invite the
              painter they already trust. Both paths lead into the same
              ColourMatch Pro engine. The invited painter receives 20% off
              their first subscription payment. */}
          <div className="mt-6 flex items-start gap-3 rounded-2xl border border-brand-linen bg-white/70 px-5 py-4 max-w-2xl" data-testid="painters-invite-strip">
            <div className="w-9 h-9 rounded-lg bg-brand-teal/10 text-brand-teal flex items-center justify-center flex-shrink-0">
              <UserPlus size={16} />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold text-brand-ink">Already have a painter you trust?</div>
              <p className="text-xs text-brand-muted mt-1 leading-relaxed">
                Invite them to join ColourMatch Pro and they&rsquo;ll receive <strong>20% off</strong> their first subscription payment.
              </p>
              <Link
                to="/referrals"
                className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-brand-teal hover:underline"
                data-testid="painters-invite-your-own-cta"
              >
                Invite your painter <ArrowRight size={12} />
              </Link>
            </div>
          </div>

          {/* Filters */}
          <div className="mt-10 grid grid-cols-1 md:grid-cols-12 gap-3">
            <div className="md:col-span-5 relative">
              <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-brand-muted" />
              <input
                type="text"
                value={q}
                onChange={(e) => { setQ(e.target.value); setPage(1); }}
                placeholder="Search by suburb or postcode (e.g. Brisbane or 4000)"
                data-testid="painters-search-input"
                className="w-full pl-11 pr-4 h-12 rounded-2xl bg-white border border-brand-border text-sm text-brand-ink placeholder:text-brand-muted/70 focus:outline-none focus:border-brand-teal focus:ring-2 focus:ring-brand-teal/15"
              />
            </div>
            <div className="md:col-span-3 relative">
              <select
                value={state}
                onChange={(e) => { setState(e.target.value); setPage(1); }}
                data-testid="painters-state-select"
                className="w-full h-12 rounded-2xl bg-white border border-brand-border text-sm text-brand-ink px-4 pr-10 appearance-none focus:outline-none focus:border-brand-teal focus:ring-2 focus:ring-brand-teal/15"
              >
                <option value="">All states</option>
                {AU_STATES.map((s) => (<option key={s} value={s}>{s}</option>))}
              </select>
              <ChevronDown size={16} className="absolute right-4 top-1/2 -translate-y-1/2 text-brand-muted pointer-events-none" />
            </div>
            <div className="md:col-span-2 relative">
              <select
                value={scope}
                onChange={(e) => { setScope(e.target.value); setPage(1); }}
                data-testid="painters-scope-select"
                className="w-full h-12 rounded-2xl bg-white border border-brand-border text-sm text-brand-ink px-4 pr-10 appearance-none focus:outline-none focus:border-brand-teal focus:ring-2 focus:ring-brand-teal/15"
              >
                <option value="">All services</option>
                <option value="interior">Interior</option>
                <option value="exterior">Exterior</option>
              </select>
              <ChevronDown size={16} className="absolute right-4 top-1/2 -translate-y-1/2 text-brand-muted pointer-events-none" />
            </div>
            <div className="md:col-span-2 relative">
              <select
                value={sort}
                onChange={(e) => { setSort(e.target.value); setPage(1); }}
                data-testid="painters-sort-select"
                className="w-full h-12 rounded-2xl bg-white border border-brand-border text-sm text-brand-ink px-4 pr-10 appearance-none focus:outline-none focus:border-brand-teal focus:ring-2 focus:ring-brand-teal/15"
              >
                <option value="recent">Most recent</option>
                <option value="business_name">Name (A–Z)</option>
              </select>
              <ChevronDown size={16} className="absolute right-4 top-1/2 -translate-y-1/2 text-brand-muted pointer-events-none" />
            </div>
          </div>
        </div>
      </section>

      {/* Results */}
      <section className="max-w-7xl mx-auto px-5 lg:px-8 py-12">
        {busy && (
          <div data-testid="painters-loading" className="text-center text-brand-muted py-20">
            Loading painters…
          </div>
        )}
        {!busy && err && (
          <div data-testid="painters-error" className="text-center bg-white border border-brand-border rounded-3xl p-12">
            <p className="text-brand-ink font-medium">{err}</p>
          </div>
        )}
        {!busy && !err && data.items.length === 0 && (() => {
          // LF-001 (2026-02-09) · Homeowner-first empty state.
          // Whether the directory is genuinely empty OR the filters just
          // returned nothing, the homeowner is never blocked — they can
          // always download their Paint Specification and share it with
          // any independent painter they trust. Painter recruitment
          // lives on /painters/join, not here.
          const filtersActive = Boolean(q.trim() || state || scope);
          return (
            <div
              data-testid="painters-empty"
              className="bg-white border border-brand-border rounded-3xl p-8 lg:p-12"
            >
              <div className="max-w-2xl">
                <Paintbrush2 size={28} className="text-brand-teal" />
                <h2 className="mt-4 text-2xl lg:text-3xl font-extrabold tracking-tight text-brand-ink leading-tight">
                  {filtersActive
                    ? "No painters match your filters yet."
                    : "We're onboarding painters in your area."}
                </h2>
                <p className="mt-3 text-brand-muted leading-relaxed">
                  You&rsquo;re never blocked. Download your Paint Specification and share it with
                  any independent painter you already trust — the colours, surfaces, and photo brief
                  are all included. It works with any painter.
                </p>
                <div className="mt-6 flex flex-col sm:flex-row gap-3">
                  <Link
                    to="/visualiser/new"
                    data-testid="painters-empty-paintspec-cta"
                    className="inline-flex items-center gap-2 bg-brand-navy hover:bg-brand-navy/90 text-white h-12 px-6 rounded-2xl text-sm font-semibold tap-spring"
                  >
                    Download Paint Specification <ArrowRight size={14} />
                  </Link>
                  {filtersActive && (
                    <button
                      type="button"
                      onClick={() => { setQ(""); setState(""); setScope(""); setPage(1); }}
                      data-testid="painters-empty-clear-filters"
                      className="inline-flex items-center gap-2 border border-brand-border hover:border-brand-teal hover:text-brand-teal text-brand-ink h-12 px-5 rounded-2xl text-sm font-semibold tap-spring"
                    >
                      Adjust filters
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })()}
        {!busy && !err && data.items.length > 0 && (
          <>
            <div className="mb-6 text-sm text-brand-muted" data-testid="painters-result-count">
              Showing {data.items.length} of {data.total} painter{data.total === 1 ? "" : "s"}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {data.items.map((p) => (
                <PainterCard key={p.id} p={p} fromProject={fromProject} />
              ))}
            </div>
            {totalPages > 1 && (
              <div className="mt-10 flex items-center justify-center gap-3" data-testid="painters-pagination">
                <button
                  onClick={() => setPage((n) => Math.max(1, n - 1))}
                  disabled={page <= 1}
                  data-testid="painters-prev-btn"
                  className="px-4 h-10 rounded-2xl border border-brand-border text-sm font-medium text-brand-ink bg-white disabled:opacity-40 hover:border-brand-teal transition-colors"
                >
                  Previous
                </button>
                <span className="text-sm text-brand-muted">
                  Page {page} of {totalPages}
                </span>
                <button
                  onClick={() => setPage((n) => Math.min(totalPages, n + 1))}
                  disabled={page >= totalPages}
                  data-testid="painters-next-btn"
                  className="px-4 h-10 rounded-2xl border border-brand-border text-sm font-medium text-brand-ink bg-white disabled:opacity-40 hover:border-brand-teal transition-colors"
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}

        {/* Slim footer strip — independent painters can list on ColourMatch.
            LF-001 (2026-02-09): full recruitment content moved to /painters/join. */}
        <div
          data-testid="painters-independent-footer"
          className="mt-16 border-t border-brand-border pt-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 text-sm"
        >
          <div className="text-brand-muted">
            Painters listed on ColourMatch are independent businesses.
          </div>
          <Link
            to="/painters/join"
            data-testid="painters-join-link"
            className="inline-flex items-center gap-1.5 text-brand-teal hover:underline font-semibold whitespace-nowrap"
          >
            Are you an independent painter? List your business on ColourMatch <ArrowRight size={13} />
          </Link>
        </div>

        {/* Visualiser cross-link — homeowner-facing */}
        <div className="mt-8 bg-white border border-brand-border rounded-3xl p-6 lg:p-8 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4">
          <div>
            <h4 className="text-lg font-bold text-brand-ink">Not sure which colours yet?</h4>
            <p className="mt-1 text-sm text-brand-muted">
              Design a complete colour scheme on a photo of your home in seconds — free.
            </p>
          </div>
          <Link
            to="/visualiser/new"
            data-testid="visualiser-cross-link"
            className="inline-flex items-center gap-2 border border-brand-border hover:border-brand-teal hover:text-brand-teal text-brand-ink h-11 px-5 rounded-2xl text-sm font-semibold tap-spring whitespace-nowrap"
          >
            Try ColourMatch <ArrowRight size={14} />
          </Link>
        </div>
      </section>
    </div>
  );
}
